import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AppConfigKeys } from '@rialtic/types'
import { useFeatureFlagsConnection } from '../composables/useFeatureFlagsConnection'
import { useNuxtApp } from '#app'
import type { RumGlobal } from '@datadog/browser-rum'

export interface FeatureFlagState {
  flags: Record<string, boolean>
  loading: boolean
  error: string | null
}

/**
 * Feature Flags Store
 *
 * Provides state and methods for managing feature flags
 * through the Cap'n Web WebSocket RPC connection to the Workers API.
 *
 * This is a singleton store - all instances share the same state.
 */
export const useFeatureFlags = defineStore('featureFlags', () => {
  const connection = useFeatureFlagsConnection()
  const { $datadog } = useNuxtApp() as unknown as {
    $datadog: RumGlobal
  }

  // State
  const flags = ref<Record<string, boolean>>({})
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Load all feature flags from the RPC endpoint
   */
  const loadFlags = async (): Promise<void> => {
    if (loading.value) return

    loading.value = true
    error.value = null

    try {
      const session = await connection.getSession()
      if (!session) {
        throw new Error('Unable to establish RPC connection')
      }

      const loadedFlags = await session.getAllFlags()
      flags.value = loadedFlags

      $datadog.addAction('feature-flags-loaded', {
        flagCount: Object.keys(loadedFlags).length,
      })
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load feature flags'
      error.value = errorMessage

      $datadog.addError(err, {
        function: 'useFeatureFlags:loadFlags',
      })
    } finally {
      loading.value = false
    }
  }

  /**
   * Get a specific feature flag value
   */
  const getFlag = async (key: AppConfigKeys): Promise<boolean> => {
    try {
      const session = await connection.getSession()
      if (!session) {
        throw new Error('Unable to establish RPC connection')
      }

      const value = await session.getFlag(key)
      flags.value[key] = value

      return value
    } catch (err) {
      $datadog.addError(err, {
        function: 'useFeatureFlags:getFlag',
        flagKey: key,
      })

      return flags.value[key] ?? false
    }
  }

  /**
   * Update a feature flag value
   */
  const updateFlag = async (
    key: AppConfigKeys,
    value: boolean,
  ): Promise<void> => {
    const prevValue = flags.value[key] ?? false
    flags.value = { ...flags.value, [key]: value }

    try {
      const session = await connection.getSession()
      if (!session) {
        throw new Error('Unable to establish RPC connection')
      }

      const updatedFlags = await session.updateFlag(key, value)
      flags.value = { ...updatedFlags, [key]: value }

      $datadog.addAction('feature-flag-updated', {
        flagKey: key,
        value,
      })
    } catch (err) {
      flags.value = { ...flags.value, [key]: prevValue }

      $datadog.addError(err, {
        function: 'useFeatureFlags:updateFlag',
        flagKey: key,
        value,
      })
      throw err
    }
  }

  /**
   * Toggle a feature flag value
   */
  const toggleFlag = async (key: AppConfigKeys): Promise<boolean> => {
    const prevValue = flags.value[key] ?? false
    const newValue = !prevValue
    flags.value = { ...flags.value, [key]: newValue }

    try {
      const session = await connection.getSession()
      if (!session) {
        throw new Error('Unable to establish RPC connection')
      }

      const updatedFlags = await session.updateFlag(key, newValue)
      flags.value = { ...updatedFlags, [key]: newValue }

      $datadog.addAction('feature-flag-toggled', {
        flagKey: key,
        newValue,
      })

      return newValue
    } catch (err) {
      flags.value = { ...flags.value, [key]: prevValue }

      $datadog.addError(err, {
        function: 'useFeatureFlags:toggleFlag',
        flagKey: key,
      })
      throw err
    }
  }

  /**
   * Update multiple feature flags at once
   */
  const updateFlags = async (
    updates: Record<AppConfigKeys, boolean>,
  ): Promise<void> => {
    try {
      const session = await connection.getSession()
      if (!session) {
        throw new Error('Unable to establish RPC connection')
      }

      await session.updateFlags(updates)
      Object.assign(flags.value, updates)

      $datadog.addAction('feature-flags-bulk-updated', {
        flagCount: Object.keys(updates).length,
        flags: Object.keys(updates),
      })
    } catch (err) {
      $datadog.addError(err, {
        function: 'useFeatureFlags:updateFlags',
        flagCount: Object.keys(updates).length,
      })
      throw err
    }
  }

  /**
   * Initialize flags on first use
   */
  const initialize = async (): Promise<void> => {
    if (!connection.isConnected.value && !connection.isConnecting.value) {
      await connection.connect()
    }

    if (Object.keys(flags.value).length === 0) {
      await loadFlags()
    }
  }

  // Computed properties
  const isReady = computed(() => connection.isConnected.value && !loading.value)

  const hasError = computed(() =>
    Boolean(error.value || connection.error.value),
  )

  const errorMessage = computed(
    () => error.value || connection.error.value || null,
  )

  /**
   * Helper to check flag with fallback
   */
  const isEnabled = (key: string, fallback = false): boolean => {
    return flags.value[key] ?? fallback
  }

  return {
    // State
    flags,
    loading,
    error: errorMessage,
    isConnected: connection.isConnected,
    isConnecting: connection.isConnecting,
    isReady,
    hasError,

    // Actions
    initialize,
    loadFlags,
    getFlag,
    updateFlag,
    toggleFlag,
    updateFlags,
    isEnabled,
    connect: connection.connect,
    disconnect: connection.disconnect,
  }
})
